import { useEffect, useRef } from "react";
import { loadConfig } from "../../config/appConfig";
import { api } from "../../services/apiService";

const CONNECT_TIMEOUT_MS = 15000;
const STALL_TIMEOUT_MS = 40000;
const POLL_INTERVAL_MS = 5000;
const POLL_PAGE_SIZE = 50;
const SSE_RETRY_INTERVAL_MS = 2 * 60 * 1000;

function mapNotificationToAlarme(n) {
    return {
        notificationId: n.id,
        parameterId: n.parameterId,
        parameterName: null,
        tremId: n.tremId,
        tremNome: n.tremNome,
        linhaId: n.linhaId,
        carro: n.carro,
        parameterValue: n.parameterValue,
        timestamp: n.messageTimestamp
    };
}

export default function useAlarmeStreamMonitor(onAlarme, enabled, onStreamError) {

    const modeRef = useRef("sse");
    const lastActivityRef = useRef(Date.now());
    const cancelledRef = useRef(false);
    const cursorRef = useRef(Date.now());
    const processedIdsRef = useRef(new Set());

    useEffect(() => {

        if (!enabled) return;

        let eventSource;
        let watchdog;
        let pollingTimeoutId;
        let pollingActive = false;
        let sseRetryInterval;
        let probeEventSource;
        cancelledRef.current = false;
        modeRef.current = "sse";
        cursorRef.current = Date.now();

        const emit = (alarme) => {
            const chave = alarme.notificationId ??
                `${alarme.tremId}-${alarme.carro}-${alarme.parameterId}-${alarme.timestamp}`;

            if (processedIdsRef.current.has(chave)) return;
            processedIdsRef.current.add(chave);

            const quando = new Date(alarme.timestamp).getTime();
            if (!Number.isNaN(quando) && quando > cursorRef.current) {
                cursorRef.current = quando;
            }

            onAlarme(alarme);
        };

        const fetchNovasNotificacoes = async () => {
            try {
                const response = await api.notifications.getAll(1, POLL_PAGE_SIZE, {
                    eventoInicio: new Date(cursorRef.current)
                });

                const notificacoes = [...(response.data || [])].reverse();
                notificacoes.forEach(n => emit(mapNotificationToAlarme(n)));

            } catch (err) {
                console.error("[Polling] erro ao buscar notificações", err);
            }
        };

        const pollingLoop = async () => {
            pollingActive = true;
            while (pollingActive && !cancelledRef.current) {
                await fetchNovasNotificacoes();
                if (!pollingActive || cancelledRef.current) break;
                await new Promise(resolve => {
                    pollingTimeoutId = setTimeout(resolve, POLL_INTERVAL_MS);
                });
            }
        };

        const stopPolling = () => {
            pollingActive = false;
            if (pollingTimeoutId) clearTimeout(pollingTimeoutId);
        };

        const startSseProbe = (config) => {

            clearInterval(sseRetryInterval);

            sseRetryInterval = setInterval(async () => {

                if (cancelledRef.current || modeRef.current === "sse") return;

                const url = await api.notifications.streamUrl();
                probeEventSource = new EventSource(url, { withCredentials: true });

                const probeTimeout = setTimeout(() => {
                    console.log("[ALARME] sonda SSE falhou, continua no polling");
                    probeEventSource?.close();
                }, CONNECT_TIMEOUT_MS);

                const sonda = probeEventSource;
                let promovida = false;

                const promoteToSse = () => {
                    if (promovida) return;
                    promovida = true;

                    sonda.removeEventListener("ping", promoteToSse);
                    sonda.onmessage = null;

                    clearTimeout(probeTimeout);

                    stopPolling();
                    clearInterval(sseRetryInterval);

                    eventSource = sonda;
                    attachMainSseHandlers(config);
                    modeRef.current = "sse";
                };

                probeEventSource.onmessage = promoteToSse;
                probeEventSource.addEventListener("ping", promoteToSse);

                probeEventSource.onerror = () => {
                    clearTimeout(probeTimeout);
                    probeEventSource?.close();
                };

            }, SSE_RETRY_INTERVAL_MS);

        };

        const startPolling = (config) => {

            if (cancelledRef.current) return;
            
            if (pollingActive) return;

            modeRef.current = "polling";
            console.log("%c[ALARME] modo atual: POLLING", "font-weight:bold;color:#e67e22;");

            pollingLoop();
            startSseProbe(config);

        };

        // Handler nomeado: addEventListener ignora registro duplicado da mesma referência.
        const marcarAtividade = () => {
            lastActivityRef.current = Date.now();
        };

        const attachMainSseHandlers = (config) => {

            lastActivityRef.current = Date.now();
            console.log("%c[ALARME] modo atual: SSE", "font-weight:bold;color:#2b7;");

            eventSource.onopen = () => {
                lastActivityRef.current = Date.now();
            };

            eventSource.onmessage = (event) => {
                lastActivityRef.current = Date.now();
                try {
                    const alarme = JSON.parse(event.data);
                    emit(alarme);
                } catch (err) {
                    console.error("[ALARME] erro ao converter evento SSE. Payload recebido.");
                    onStreamError?.(err);
                }
            };

            eventSource.addEventListener("ping", marcarAtividade);

            eventSource.onerror = () => {
                console.log("[ALARME] SSE error/onerror — trocando para polling");
                eventSource.close();
                clearInterval(watchdog);
                startPolling(config);
            };

            clearInterval(watchdog);
            watchdog = setInterval(() => {
                const idle = Date.now() - lastActivityRef.current;
                const limite = eventSource.readyState === 1 ? STALL_TIMEOUT_MS : CONNECT_TIMEOUT_MS;

                if (idle > limite) {
                    console.log(`[ALARME] SSE travado (${idle}ms sem atividade, readyState=${eventSource.readyState}) — trocando para polling`);
                    eventSource.close();
                    clearInterval(watchdog);
                    startPolling(config);
                }
            }, 3000);

        };

        const tryEventSource = async () => {

            if (cancelledRef.current) return;

            const config = await loadConfig();
            const url = await api.notifications.streamUrl();

            eventSource = new EventSource(url, { withCredentials: true });
            attachMainSseHandlers(config);

        };

        tryEventSource();

        return () => {
            cancelledRef.current = true;
            stopPolling();
            clearInterval(watchdog);
            clearInterval(sseRetryInterval);
            eventSource?.close();
            probeEventSource?.close();
        };

    }, [enabled, onAlarme, onStreamError]);
}
